/**
 * SDG Mapper Service
 * Maps UN Sustainable Development Goal numbers to structured information
 */

export interface SDGMapping {
    number: string;
    name: string;
    fullName: string;
}

/**
 * Complete mapping of all 17 UN Sustainable Development Goals
 * Source: United Nations - https://sdgs.un.org/goals
 */
export const SDG_MAPPINGS: Record<string, SDGMapping> = {
    "1": { number: "1", name: "No Poverty", fullName: "SDG 1: No Poverty" },
    "2": { number: "2", name: "Zero Hunger", fullName: "SDG 2: Zero Hunger" },
    "3": { number: "3", name: "Good Health and Well-being", fullName: "SDG 3: Good Health and Well-being" },
    "4": { number: "4", name: "Quality Education", fullName: "SDG 4: Quality Education" },
    "5": { number: "5", name: "Gender Equality", fullName: "SDG 5: Gender Equality" },
    "6": { number: "6", name: "Clean Water and Sanitation", fullName: "SDG 6: Clean Water and Sanitation" },
    "7": { number: "7", name: "Affordable and Clean Energy", fullName: "SDG 7: Affordable and Clean Energy" },
    "8": { number: "8", name: "Decent Work and Economic Growth", fullName: "SDG 8: Decent Work and Economic Growth" },
    "9": { number: "9", name: "Industry, Innovation and Infrastructure", fullName: "SDG 9: Industry, Innovation and Infrastructure" },
    "10": { number: "10", name: "Reduced Inequalities", fullName: "SDG 10: Reduced Inequalities" },
    "11": { number: "11", name: "Sustainable Cities and Communities", fullName: "SDG 11: Sustainable Cities and Communities" },
    "12": { number: "12", name: "Responsible Consumption and Production", fullName: "SDG 12: Responsible Consumption and Production" },
    "13": { number: "13", name: "Climate Action", fullName: "SDG 13: Climate Action" },
    "14": { number: "14", name: "Life Below Water", fullName: "SDG 14: Life Below Water" },
    "15": { number: "15", name: "Life on Land", fullName: "SDG 15: Life on Land" },
    "16": { number: "16", name: "Peace, Justice and Strong Institutions", fullName: "SDG 16: Peace, Justice and Strong Institutions" },
    "17": { number: "17", name: "Partnerships for the Goals", fullName: "SDG 17: Partnerships for the Goals" }
};

/**
 * Map array of SDG numbers to SDG objects
 * Filters out invalid numbers (not in range 1-17)
 *
 * @param sdgNumbers - Array of SDG numbers (e.g., [7, 11, 13])
 * @returns Array of SDG mappings with full information
 *
 * @example
 * parseSDGNumbers([7, 11, 13])
 * // Returns: [
 * //   { number: "7", name: "Affordable and Clean Energy", fullName: "SDG 7: Affordable and Clean Energy" },
 * //   { number: "11", name: "Sustainable Cities and Communities", fullName: "SDG 11: Sustainable Cities and Communities" },
 * //   { number: "13", name: "Climate Action", fullName: "SDG 13: Climate Action" }
 * // ]
 *
 * @example
 * parseSDGNumbers([7, 99, 13])  // Invalid 99 filtered out
 * // Returns: [{ number: "7", ... }, { number: "13", ... }]
 *
 * @example
 * parseSDGNumbers([]) // Empty array
 * // Returns: []
 */
export function parseSDGNumbers(sdgNumbers?: number[]): SDGMapping[] {
    if (!sdgNumbers || sdgNumbers.length === 0) return [];

    return sdgNumbers
        .filter(num => num >= 1 && num <= 17 && SDG_MAPPINGS[num.toString()])
        .map(num => SDG_MAPPINGS[num.toString()]);
}

/**
 * Format SDG numbers as comma-separated display text
 *
 * @param sdgNumbers - Array of SDG numbers
 * @returns Formatted string of full SDG names
 *
 * @example
 * formatSDGDisplay([7, 11, 13])
 * // Returns: "SDG 7: Affordable and Clean Energy, SDG 11: Sustainable Cities and Communities, SDG 13: Climate Action"
 *
 * @example
 * formatSDGDisplay([])
 * // Returns: ""
 */
export function formatSDGDisplay(sdgNumbers?: number[]): string {
    const mappings = parseSDGNumbers(sdgNumbers);
    return mappings.map(m => m.fullName).join(', ');
}
